import { useEffect, useRef, useState } from "react";
import { BellSimple, BellRinging, Check, Info, Buildings } from "@phosphor-icons/react";
import { AccountButton, AccountDrawer, AccountIconButton, AccountInput } from "../../components/shared/account/AccountUI.tsx";
import LoadingRing, { PendingRing } from "../../components/shared/LoadingRing.jsx";
import { t, useStoreLanguage, getDirection, localizeDigits } from "../store-language/i18n.ts";
import { useNotificationInbox } from "./useNotificationInbox.ts";
import { normalizeSearch, notificationText, safeNotificationHtml } from "./notificationHtml.ts";
import { notificationTypes, notificationTypeLabel } from "./notificationApi.ts";
import { notificationDate } from "./NotificationDetail.tsx";
import "./notifications.css";
import useMobileGrid from "../../components/shared/grid/useMobileGrid.ts";

export default function NotificationCenter() {
  useStoreLanguage();
  const mobile = useMobileGrid();
  const { records, loading, pending, mark, session, failed } = useNotificationInbox();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [type, setType] = useState(0);
  const drag = useRef({ active: false, moved: false, x: 0, scroll: 0 });
  const unread = records.filter(function (record) { return !record.readAt; }).length;
  const visible = records.filter(function (record) {
    return (!type || record.type === type) && normalizeSearch(`${record.title} ${notificationText(record.message)} ${t(notificationTypeLabel(record.type))}`).includes(normalizeSearch(query));
  });
  useEffect(function () { setOpen(false); setQuery(""); setType(0); }, [session]);

  function title(value: string) {
    const index = normalizeSearch(value).indexOf(normalizeSearch(query));
    if (!query.trim() || index < 0) return value;
    return <>{value.slice(0, index)}<mark>{value.slice(index, index + query.trim().length)}</mark>{value.slice(index + query.trim().length)}</>;
  }

  return (
    <span className="utility-notifications">
      <AccountIconButton
        label={t("اعلان‌ها")}
        type="text"
        icon={unread ? (
          <BellRinging size={22} weight="duotone" />
        ) : (
          <BellSimple size={22} weight="duotone" />
        )}
        aria-expanded={open}
        onClick={function () { setOpen(true); }}
      />
      {unread > 0 ? <span className="utility-notifications__count">{localizeDigits(unread > 99 ? "99+" : unread)}</span> : null}
      <AccountDrawer
        title={(
          <span className="notification-center__header">
            <span className="notification-center__header-symbol"><BellRinging size={22} /></span>
            <span className="notification-center__header-copy">
              <span className="notification-center__header-title">{t("مرکز اعلان‌ها")}</span>
              <span className="notification-center__header-description">
                {unread ? t("{{count}} مورد نیازمند توجه شماست", { count: unread }) : t("همه اعلان‌ها بررسی شده‌اند")}
              </span>
            </span>
          </span>
        )}
        open={open}
        onClose={function () { if (!pending) setOpen(false); }}
        placement={mobile ? "right" : getDirection() === "rtl" ? "left" : "right"}
        width={mobile ? "min(22rem, calc(100vw - 2rem))" : "min(30rem, 100vw)"}
        busy={pending !== null}
        flush
        headerActions={unread ? (
          <AccountButton
            type="text"
            icon={<Check size={16} />}
            loading={pending === "all"}
            disabled={pending !== null}
            onClick={function () { void mark(); }}
          >{t("خواندن همه")}</AccountButton>
        ) : null}
        footer={<span className="notification-center__footer">{t("{{count}} اعلان نمایش داده می‌شود", { count: visible.length })}</span>}
      >
        <section className="notification-center" aria-label={t("مرکز اعلان‌ها")}>
          <div className="notification-center__tools">
            <div className="notification-center__search">
              <AccountInput
                autoFocus
                aria-label={t("جست‌وجو در اعلان‌ها")}
                placeholder={t("جست‌وجو در عنوان، متن یا نوع اعلان…")}
                value={query}
                onChange={function (event) { setQuery(event.target.value); }}
              />
            </div>
            <div
              className="notification-center__filters"
              onPointerDown={function (event) { drag.current = { active: true, moved: false, x: event.clientX, scroll: event.currentTarget.scrollLeft }; }}
              onPointerMove={function (event) {
                if (!drag.current.active || Math.abs(event.clientX - drag.current.x) < 8) return;
                drag.current.moved = true;
                event.currentTarget.setPointerCapture(event.pointerId);
                event.currentTarget.scrollLeft = drag.current.scroll - (event.clientX - drag.current.x);
              }}
              onPointerUp={function (event) {
                drag.current.active = false;
                if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
              }}
              onPointerCancel={function () { drag.current.active = false; }}
              onClickCapture={function (event) { if (drag.current.moved) { event.preventDefault(); event.stopPropagation(); drag.current.moved = false; } }}
              onWheel={function (event) { event.currentTarget.scrollLeft += event.deltaY * (getDirection() === "rtl" ? -1 : 1); }}
            >
              {[{ value: 0, label: "همه نوع‌ها" }, ...notificationTypes].map(function (option) {
                const count = records.filter(function (record) { return !option.value || record.type === option.value; }).length;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={type === option.value}
                    className={type === option.value ? "is-selected" : ""}
                    onClick={function () { setType(option.value); }}
                  >{t(option.label)} <span>{localizeDigits(count)}</span></button>
                );
              })}
            </div>
          </div>
          <div className="notification-center__list" aria-live="polite">
            {loading ? <div className="notification-center__loader"><LoadingRing tip={t("در حال دریافت اعلان‌ها")} /></div> : visible.length ? visible.map(function (record) {
              return (
                <article key={record.id} className={`notification-card${record.readAt ? "" : " is-unread"}`}>
                  <button
                    type="button"
                    className="notification-card__button"
                    disabled={pending !== null}
                    aria-busy={pending === record.id}
                    onClick={function () { if (!record.readAt) void mark(record.id); }}
                  >
                    <span className="notification-card__marker" aria-hidden="true" />
                    <span className="notification-card__avatar">{pending === record.id ? <PendingRing /> : <Buildings size={32} />}</span>
                    <span className="notification-card__content">
                      <span className="notification-card__sender-row">
                        <strong>{t("مدیریت مرکزی پورتال")}</strong>
                        <time>{notificationDate(record.sentAt)}</time>
                      </span>
                      <span className="notification-card__title">{title(record.title)}</span>
                      <span
                        className="notification-card__message"
                        lang={record.languageCode}
                        dir={record.languageCode === "en" ? "ltr" : "rtl"}
                        dangerouslySetInnerHTML={{ __html: safeNotificationHtml(record.message) }}
                      />
                      <span className="notification-card__meta">
                        <span>{t(notificationTypeLabel(record.type))}</span>
                        {!record.readAt ? <strong>{t("جدید")}</strong> : null}
                      </span>
                    </span>
                  </button>
                </article>
              );
            }) : (
              <div className="notification-center__state" role="status">
                <Info size={32} />
                <span>{failed ? t("دریافت تاریخچه اعلان‌ها انجام نشد.") : query || type ? t("اعلانی مطابق جست‌وجو و فیلترها پیدا نشد.") : t("هنوز اعلانی برای شما ثبت نشده است.")}</span>
                {query || type ? <AccountButton onClick={function () { setQuery(""); setType(0); }}>{t("پاک کردن فیلترها")}</AccountButton> : null}
              </div>
            )}
          </div>
        </section>
      </AccountDrawer>
    </span>
  );
}
